/**
 * SharePoint Document Service
 * Handles document operations via Microsoft Graph API
 *
 * SharePoint Site: motivenation.sharepoint.com/sites/Flow
 */

import { getMsalInstance } from '@/providers/MsalProvider';
import { graphScopes } from '@/lib/msalConfig';
import {
  MAX_ATTEMPTS,
  httpErrorMessage,
  isNetworkError,
  isRetriableStatus,
  networkErrorMessage,
  parseRetryAfterMs,
  retryDelayMs,
} from '@/lib/uploadRetry';

// SharePoint configuration
const SHAREPOINT_CONFIG = {
  hostname: import.meta.env.VITE_SHAREPOINT_HOSTNAME || 'motivenation.sharepoint.com',
  sitePath: import.meta.env.VITE_SHAREPOINT_SITE_PATH || '/sites/Flow',
};

// Graph API base URL
const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';

// Types
export interface SharePointFile {
  id: string;
  name: string;
  size: number;
  webUrl: string;
  createdDateTime: string;
  lastModifiedDateTime: string;
  createdBy?: {
    user?: {
      displayName: string;
      email: string;
    };
  };
  lastModifiedBy?: {
    user?: {
      displayName: string;
      email: string;
    };
  };
  file?: {
    mimeType: string;
  };
  folder?: {
    childCount: number;
  };
  parentReference?: {
    driveId: string;
    path: string;
  };
}

export interface SharePointFolder {
  id: string;
  name: string;
  webUrl: string;
  childCount: number;
  createdDateTime: string;
}

export interface SharePointSite {
  id: string;
  name: string;
  displayName: string;
  webUrl: string;
}

export interface SharePointDrive {
  id: string;
  name: string;
  driveType: string;
  webUrl: string;
}

export interface UploadResult {
  id: string;
  name: string;
  webUrl: string;
  size: number;
}

// =============================================================================
// TOKEN MANAGEMENT
// =============================================================================

/**
 * Get access token for SharePoint/Graph API
 * Uses silent acquisition first, then falls back to redirect (not popup) to avoid browser blocking
 */
async function getAccessToken(): Promise<string> {
  const msalInstance = getMsalInstance();
  if (!msalInstance) {
    throw new Error('Microsoft authentication is not configured.');
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new Error('No authenticated user. Please sign in first.');
  }

  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: graphScopes.sharePoint,
      account: accounts[0],
    });
    return result.accessToken;
  } catch (error) {
    console.warn('[SharePoint] Silent token acquisition failed, redirecting to login...');
    // Store the current URL to return to after auth
    sessionStorage.setItem('sharepoint_redirect_url', window.location.href);
    // Use redirect instead of popup - popups get blocked when not from direct user action
    await msalInstance.acquireTokenRedirect({
      scopes: graphScopes.sharePoint,
    });
    // This won't be reached as redirect navigates away
    throw new Error('Redirecting to Microsoft login...');
  }
}

/**
 * Check (and optionally establish) SharePoint access for the current account.
 *
 * Silent by default: a background/mount check must NEVER open a popup, because
 * Safari blocks popups that aren't tied to a user gesture — the popup just
 * returns null ("empty_window_error") and spams the console without ever
 * succeeding. Pass `interactive: true` only from a real click handler, where a
 * prompt is allowed. With Sites.ReadWrite.All now requested at sign-in, the
 * silent path normally succeeds and no prompt is needed at all.
 */
export async function ensureSharePointAuth(interactive = false): Promise<boolean> {
  const msalInstance = getMsalInstance();
  if (!msalInstance) {
    return false;
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    return false;
  }

  try {
    await msalInstance.acquireTokenSilent({
      scopes: graphScopes.sharePoint,
      account: accounts[0],
    });
    return true;
  } catch {
    if (!interactive) {
      // Background check — report "not connected" instead of attempting a popup
      // the browser will block. The caller can prompt from a user gesture.
      return false;
    }
    try {
      await msalInstance.acquireTokenPopup({
        scopes: graphScopes.sharePoint,
      });
      return true;
    } catch (popupError) {
      console.error('[SharePoint] Failed to acquire token:', popupError);
      return false;
    }
  }
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Error thrown for a Graph response that arrived but wasn't ok. Carries the
 * status so callers can branch on it — getFolderByPath needs to tell a real 404
 * apart from an expired token, which it previously could not.
 */
export class SharePointHttpError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body?: string,
  ) {
    super(message);
    this.name = 'SharePointHttpError';
  }
}

/** Pull the status off an error if it came from Graph. */
export function sharePointErrorStatus(err: unknown): number | null {
  return err instanceof SharePointHttpError ? err.status : null;
}

/**
 * Read a response body, treating a mid-read failure as retriable.
 *
 * This is not paranoia: a body read is a second trip over the same socket, so a
 * connection that drops after the headers arrive rejects here — with the same
 * bare "Load failed" a failed fetch gives — even though the request succeeded.
 * For an upload that means SharePoint has the file while the UI calls it
 * failed, so it matters that the caller retries rather than gives up.
 */
async function readBody<T>(
  response: Response,
  label: string,
  as: 'json' | 'text',
): Promise<T> {
  try {
    return as === 'json'
      ? ((await response.json()) as T)
      : ((await response.text()) as unknown as T);
  } catch (err) {
    if (isNetworkError(err)) {
      throw new Error(networkErrorMessage(err, `reading the response to ${label}`));
    }
    throw err;
  }
}

/**
 * Single HTTP attempt loop shared by every Graph call in this file.
 *
 * Retries a rejected fetch (dropped connection — the failure that used to kill
 * a whole AI Import batch) and the transient statuses, honoring Retry-After.
 * Everything else is returned to the caller on the first response.
 *
 * Callers must only pass re-readable bodies (string, Blob, File — all of which
 * we use). A stream body could not survive a second attempt.
 */
async function fetchWithRetry(
  url: string,
  init: RequestInit,
  label: string,
  attempts = MAX_ATTEMPTS,
): Promise<Response> {
  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      const response = await fetch(url, init);
      if (response.ok || !isRetriableStatus(response.status)) return response;

      // Retriable status: read the body for the message, then back off.
      const body = await response.text().catch(() => '');
      lastError = new SharePointHttpError(
        httpErrorMessage(label, response.status, body),
        response.status,
        body,
      );
      if (attempt === attempts) return response;
      const retryAfter = parseRetryAfterMs(response.headers.get('Retry-After'), Date.now());
      console.warn(
        `[SharePoint] ${label} → ${response.status}, retrying (${attempt}/${attempts - 1})`,
      );
      await sleep(retryDelayMs(attempt, retryAfter));
    } catch (err) {
      if (!isNetworkError(err)) throw err;
      lastError = new Error(networkErrorMessage(err, label));
      if (attempt === attempts) throw lastError;
      console.warn(
        `[SharePoint] ${label} network failure, retrying (${attempt}/${attempts - 1})`,
        err,
      );
      await sleep(retryDelayMs(attempt));
    }
  }

  throw lastError ?? new Error(`${label} failed`);
}

/**
 * Make authenticated request to Graph API
 */
async function graphFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAccessToken();
  const label = `${options.method ?? 'GET'} ${endpoint.split('?')[0]}`;

  const response = await fetchWithRetry(
    `${GRAPH_BASE}${endpoint}`,
    {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    },
    label,
  );

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    console.error('[SharePoint] API Error:', response.status, errorBody);
    throw new SharePointHttpError(
      httpErrorMessage(label, response.status, errorBody),
      response.status,
      errorBody,
    );
  }

  // Handle no-content responses
  if (response.status === 204) {
    return {} as T;
  }

  return readBody<T>(response, label, 'json');
}

// =============================================================================
// SITE & DRIVE OPERATIONS
// =============================================================================

/**
 * Get the SharePoint site info
 */
export async function getSite(): Promise<SharePointSite> {
  const siteId = `${SHAREPOINT_CONFIG.hostname}:${SHAREPOINT_CONFIG.sitePath}`;
  try {
    return await graphFetch<SharePointSite>(`/sites/${siteId}`);
  } catch (err) {
    // Only a misconfigured hostname/site path should reach here, and with the
    // drive memoized below this now runs at most once per session.
    console.error('[SharePoint] Site not found:', siteId);
    throw err;
  }
}

/**
 * Memoized site + drive lookup.
 *
 * These two IDs are fixed for the tenant, but every call that touched a drive
 * re-derived them — two Graph round-trips each. A 7-file AI Import submit spent
 * ~24 requests just re-answering "which drive?", which is both slow and, at
 * that volume, a real source of the dropped connections this file now retries.
 *
 * The *promise* is cached (not just the value) so concurrent callers share one
 * round-trip, and it is keyed to the signed-in account so switching Microsoft
 * accounts mid-session can't serve the previous tenant's drive. A rejection
 * clears the entry so a failed lookup is never sticky.
 */
let driveCache: { key: string; promise: Promise<SharePointDrive> } | null = null;

function currentAccountKey(): string {
  return getMsalInstance()?.getAllAccounts()[0]?.homeAccountId ?? 'none';
}

/** Drop the memoized drive — call on sign-out or account switch. */
export function clearSharePointCache(): void {
  driveCache = null;
}

/**
 * Get the default document library (drive) for the site
 */
export async function getDefaultDrive(): Promise<SharePointDrive> {
  const key = currentAccountKey();
  if (driveCache?.key === key) return driveCache.promise;

  const promise = (async () => {
    const site = await getSite();
    return graphFetch<SharePointDrive>(`/sites/${site.id}/drive`);
  })();

  driveCache = { key, promise };
  promise.catch(() => {
    if (driveCache?.promise === promise) driveCache = null;
  });
  return promise;
}

/**
 * Get all document libraries for the site
 */
export async function getDrives(): Promise<SharePointDrive[]> {
  const site = await getSite();
  const result = await graphFetch<{ value: SharePointDrive[] }>(
    `/sites/${site.id}/drives`
  );
  return result.value;
}

// =============================================================================
// FOLDER OPERATIONS
// =============================================================================

/**
 * List items in root of default document library
 */
export async function listRootItems(): Promise<SharePointFile[]> {
  const drive = await getDefaultDrive();
  const result = await graphFetch<{ value: SharePointFile[] }>(
    `/drives/${drive.id}/root/children?$select=id,name,size,webUrl,createdDateTime,lastModifiedDateTime,createdBy,lastModifiedBy,file,folder,parentReference`
  );
  return result.value;
}

/**
 * List items in a specific folder
 */
export async function listFolderItems(folderId: string): Promise<SharePointFile[]> {
  const drive = await getDefaultDrive();
  const result = await graphFetch<{ value: SharePointFile[] }>(
    `/drives/${drive.id}/items/${folderId}/children?$select=id,name,size,webUrl,createdDateTime,lastModifiedDateTime,createdBy,lastModifiedBy,file,folder,parentReference`
  );
  return result.value;
}

/**
 * List items by folder path (e.g., "/Deals/Deal-123")
 */
export async function listItemsByPath(folderPath: string): Promise<SharePointFile[]> {
  const drive = await getDefaultDrive();
  const encodedPath = encodeURIComponent(folderPath);
  const result = await graphFetch<{ value: SharePointFile[] }>(
    `/drives/${drive.id}/root:${encodedPath}:/children?$select=id,name,size,webUrl,createdDateTime,lastModifiedDateTime,createdBy,lastModifiedBy,file,folder,parentReference`
  );
  return result.value;
}

/**
 * Get folder by path
 */
export async function getFolderByPath(folderPath: string): Promise<SharePointFile | null> {
  try {
    const drive = await getDefaultDrive();
    const encodedPath = encodeURIComponent(folderPath);
    return await graphFetch<SharePointFile>(
      `/drives/${drive.id}/root:${encodedPath}`
    );
  } catch (error) {
    // Only a 404 means "folder doesn't exist". Swallowing everything else made
    // an expired token or a throttled tenant look like a missing folder, so
    // createFolderByPath would try to create a folder that was already there
    // and the real problem surfaced later as something unrelated.
    if (sharePointErrorStatus(error) === 404) return null;
    throw error;
  }
}

/**
 * Create a folder
 */
export async function createFolder(
  folderName: string,
  parentFolderId?: string
): Promise<SharePointFile> {
  const drive = await getDefaultDrive();

  const endpoint = parentFolderId
    ? `/drives/${drive.id}/items/${parentFolderId}/children`
    : `/drives/${drive.id}/root/children`;

  return graphFetch<SharePointFile>(endpoint, {
    method: 'POST',
    body: JSON.stringify({
      name: folderName,
      folder: {},
      '@microsoft.graph.conflictBehavior': 'rename',
    }),
  });
}

/**
 * Create a folder by path (creates parent folders if needed)
 */
export async function createFolderByPath(folderPath: string): Promise<SharePointFile> {
  // Split path and create each folder
  const parts = folderPath.split('/').filter(Boolean);
  let currentPath = '';
  let lastFolder: SharePointFile | null = null;

  for (const part of parts) {
    currentPath += `/${part}`;
    const existing = await getFolderByPath(currentPath);

    if (!existing) {
      // Create this folder
      const parentPath = currentPath.substring(0, currentPath.lastIndexOf('/'));
      const parentFolder = parentPath ? await getFolderByPath(parentPath) : null;

      lastFolder = await createFolder(part, parentFolder?.id);
    } else {
      lastFolder = existing;
    }
  }

  return lastFolder!;
}

// =============================================================================
// FILE OPERATIONS
// =============================================================================

/** How Graph should behave when the target name already exists. */
export type ConflictBehavior = 'replace' | 'rename' | 'fail';

export interface UploadOptions {
  /**
   * Defaults preserve each path's historical behavior: the small-file PUT has
   * always replaced (Graph's default for simple upload) and the large-file
   * session has always renamed.
   *
   * Pass 'replace' when the caller needs the upload to be *idempotent* — that
   * is what makes a retried large upload overwrite the same item instead of
   * leaving a "<name> 1.pdf" duplicate behind. Safe whenever the destination
   * folder is owned by the caller (e.g. an AI Import session folder).
   */
  conflictBehavior?: ConflictBehavior;
}

/** Graph switches from a simple PUT to a resumable session above 4 MB. */
const LARGE_FILE_THRESHOLD_BYTES = 4 * 1024 * 1024;

/**
 * Upload a file to a folder
 */
export async function uploadFile(
  file: File,
  folderPath: string,
  options: UploadOptions = {}
): Promise<UploadResult> {
  // Branch before resolving anything: large files used to pay for a drive
  // lookup and a token acquisition that uploadLargeFile then threw away and
  // re-acquired, doubling the request count on the slowest files in a batch.
  if (file.size > LARGE_FILE_THRESHOLD_BYTES) {
    return uploadLargeFile(file, folderPath, options);
  }

  const drive = await getDefaultDrive();
  const token = await getAccessToken();

  // Small file upload. Retrying this PUT is safe: it addresses a fixed path
  // with conflictBehavior=replace, so a second attempt overwrites the same
  // driveItem rather than creating a copy — which matters because a body-read
  // failure can reject after SharePoint already stored the file.
  const encodedPath = encodeURIComponent(`${folderPath}/${file.name}`);
  const conflict = options.conflictBehavior ?? 'replace';
  const label = `upload ${file.name}`;
  const response = await fetchWithRetry(
    `${GRAPH_BASE}/drives/${drive.id}/root:${encodedPath}:/content` +
      `?@microsoft.graph.conflictBehavior=${conflict}`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file,
    },
    label,
  );

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new SharePointHttpError(
      httpErrorMessage(label, response.status, errorBody),
      response.status,
      errorBody,
    );
  }

  return readBody<UploadResult>(response, label, 'json');
}

/**
 * Upload a large file using upload session (supports files up to 250GB)
 *
 * Retries happen per chunk, never around the whole upload: Graph's resumable
 * session lets the same byte range be re-PUT safely, whereas re-running the
 * whole thing under the default conflictBehavior 'rename' would commit a
 * duplicate item. Callers who want whole-file retry must pass
 * conflictBehavior: 'replace'.
 */
async function uploadLargeFile(
  file: File,
  folderPath: string,
  options: UploadOptions = {}
): Promise<UploadResult> {
  const drive = await getDefaultDrive();
  const token = await getAccessToken();
  const label = `upload ${file.name}`;

  // Create upload session
  const encodedPath = encodeURIComponent(`${folderPath}/${file.name}`);
  const sessionResponse = await fetchWithRetry(
    `${GRAPH_BASE}/drives/${drive.id}/root:${encodedPath}:/createUploadSession`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        item: {
          '@microsoft.graph.conflictBehavior': options.conflictBehavior ?? 'rename',
        },
      }),
    },
    `${label} (start)`,
  );

  if (!sessionResponse.ok) {
    const errorBody = await sessionResponse.text().catch(() => '');
    throw new SharePointHttpError(
      httpErrorMessage(`${label} (start)`, sessionResponse.status, errorBody),
      sessionResponse.status,
      errorBody,
    );
  }

  const session = await readBody<{ uploadUrl: string }>(
    sessionResponse,
    `${label} (start)`,
    'json',
  );
  const uploadUrl = session.uploadUrl;

  // Upload in chunks (10MB chunks)
  const chunkSize = 10 * 1024 * 1024;
  const totalSize = file.size;
  let offset = 0;
  let result: UploadResult | null = null;

  while (offset < totalSize) {
    const end = Math.min(offset + chunkSize, totalSize);
    const chunk = file.slice(offset, end);

    // No Authorization header: the session URL carries its own credentials.
    const uploadResponse = await fetchWithRetry(
      uploadUrl,
      {
        method: 'PUT',
        headers: {
          'Content-Length': String(end - offset),
          'Content-Range': `bytes ${offset}-${end - 1}/${totalSize}`,
        },
        body: chunk,
      },
      `${label} (bytes ${offset}-${end - 1})`,
    );

    if (uploadResponse.status === 200 || uploadResponse.status === 201) {
      // Upload complete
      result = await readBody<UploadResult>(uploadResponse, label, 'json');
      break;
    }

    if (uploadResponse.status !== 202) {
      const errorBody = await uploadResponse.text().catch(() => '');
      throw new SharePointHttpError(
        httpErrorMessage(label, uploadResponse.status, errorBody),
        uploadResponse.status,
        errorBody,
      );
    }

    // 202 Accepted: trust the server's nextExpectedRanges over our own cursor
    // so a chunk Graph only partially received is re-sent from the byte it
    // actually wants rather than skipped. Ignore a range that doesn't advance
    // past the current offset — that would spin this loop forever.
    const next = await nextOffsetFromAccepted(uploadResponse);
    offset = next !== null && next > offset ? next : end;
  }

  if (!result) {
    throw new Error(
      `${label} finished sending every byte but SharePoint never confirmed the file.`,
    );
  }

  return result;
}

/**
 * Read `nextExpectedRanges` off a 202 and turn it into the next byte offset.
 * Returns null when the body is absent or unparseable — normal enough that the
 * caller just keeps its own cursor.
 */
async function nextOffsetFromAccepted(response: Response): Promise<number | null> {
  try {
    const body = (await response.json()) as { nextExpectedRanges?: string[] };
    const next = Number(body?.nextExpectedRanges?.[0]?.split('-')[0]);
    return Number.isFinite(next) && next >= 0 ? next : null;
  } catch {
    return null;
  }
}

/**
 * Download a file
 */
export async function downloadFile(fileId: string): Promise<Blob> {
  const drive = await getDefaultDrive();
  const token = await getAccessToken();

  const response = await fetch(
    `${GRAPH_BASE}/drives/${drive.id}/items/${fileId}/content`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Download failed: ${response.status}`);
  }

  return response.blob();
}

/**
 * Get file download URL (for direct browser download)
 */
export async function getDownloadUrl(fileId: string): Promise<string> {
  const drive = await getDefaultDrive();
  const result = await graphFetch<{ '@microsoft.graph.downloadUrl': string }>(
    `/drives/${drive.id}/items/${fileId}?$select=@microsoft.graph.downloadUrl`
  );
  return result['@microsoft.graph.downloadUrl'];
}

/**
 * Delete a file or folder
 */
export async function deleteItem(itemId: string): Promise<void> {
  const drive = await getDefaultDrive();
  await graphFetch(`/drives/${drive.id}/items/${itemId}`, {
    method: 'DELETE',
  });
}

/**
 * Rename a file or folder
 */
export async function renameItem(
  itemId: string,
  newName: string
): Promise<SharePointFile> {
  const drive = await getDefaultDrive();
  return graphFetch<SharePointFile>(`/drives/${drive.id}/items/${itemId}`, {
    method: 'PATCH',
    body: JSON.stringify({ name: newName }),
  });
}

/**
 * Move a file or folder
 */
export async function moveItem(
  itemId: string,
  destinationFolderId: string
): Promise<SharePointFile> {
  const drive = await getDefaultDrive();
  return graphFetch<SharePointFile>(`/drives/${drive.id}/items/${itemId}`, {
    method: 'PATCH',
    body: JSON.stringify({
      parentReference: {
        id: destinationFolderId,
      },
    }),
  });
}

/**
 * Copy a file or folder
 */
export async function copyItem(
  itemId: string,
  destinationFolderId: string,
  newName?: string
): Promise<void> {
  const drive = await getDefaultDrive();
  await graphFetch(`/drives/${drive.id}/items/${itemId}/copy`, {
    method: 'POST',
    body: JSON.stringify({
      parentReference: {
        driveId: drive.id,
        id: destinationFolderId,
      },
      name: newName,
    }),
  });
}

// =============================================================================
// DEAL-SPECIFIC OPERATIONS
// =============================================================================

/**
 * Default folder structure for deals
 */
const DEAL_FOLDER_TEMPLATE = [
  'Due Diligence',
  'Financials',
  'Legal',
  'Photos',
  'Closing',
];

/**
 * Create deal folder structure
 */
export async function createDealFolder(
  dealId: string,
  dealName: string
): Promise<SharePointFile> {
  // Sanitize deal name for folder
  const safeName = dealName.replace(/[<>:"/\\|?*]/g, '-');
  const folderName = `${dealId} - ${safeName}`;
  const folderPath = `/Deals/${folderName}`;

  // Create main deal folder
  const dealFolder = await createFolderByPath(folderPath);

  // Create subfolders
  for (const subfolder of DEAL_FOLDER_TEMPLATE) {
    await createFolder(subfolder, dealFolder.id);
  }

  return dealFolder;
}

/**
 * Get or create deal folder
 */
export async function getDealFolder(
  dealId: string,
  dealName: string
): Promise<SharePointFile> {
  const safeName = dealName.replace(/[<>:"/\\|?*]/g, '-');
  const folderPath = `/Deals/${dealId} - ${safeName}`;

  const existing = await getFolderByPath(folderPath);
  if (existing) {
    return existing;
  }

  return createDealFolder(dealId, dealName);
}

/**
 * List files in a deal folder
 */
export async function listDealFiles(
  dealId: string,
  dealName: string,
  subfolder?: string
): Promise<SharePointFile[]> {
  const safeName = dealName.replace(/[<>:"/\\|?*]/g, '-');
  let folderPath = `/Deals/${dealId} - ${safeName}`;
  if (subfolder) {
    folderPath += `/${subfolder}`;
  }

  try {
    return await listItemsByPath(folderPath);
  } catch (error) {
    // Folder doesn't exist yet
    return [];
  }
}

/**
 * Upload file to deal folder
 */
export async function uploadDealFile(
  dealId: string,
  dealName: string,
  file: File,
  subfolder?: string
): Promise<UploadResult> {
  const safeName = dealName.replace(/[<>:"/\\|?*]/g, '-');
  let folderPath = `/Deals/${dealId} - ${safeName}`;
  if (subfolder) {
    folderPath += `/${subfolder}`;
  }

  // Ensure folder exists
  await createFolderByPath(folderPath);

  return uploadFile(file, folderPath);
}

/**
 * Create a custom folder within a deal folder
 */
export async function createDealSubfolder(
  dealId: string,
  dealName: string,
  folderName: string,
  parentSubfolder?: string
): Promise<SharePointFile> {
  const safeName = dealName.replace(/[<>:"/\\|?*]/g, '-');
  let folderPath = `/Deals/${dealId} - ${safeName}`;
  if (parentSubfolder) {
    folderPath += `/${parentSubfolder}`;
  }
  folderPath += `/${folderName}`;

  return createFolderByPath(folderPath);
}

// =============================================================================
// SEARCH
// =============================================================================

/**
 * Search for files in the site
 */
export async function searchFiles(query: string): Promise<SharePointFile[]> {
  const drive = await getDefaultDrive();
  const result = await graphFetch<{ value: SharePointFile[] }>(
    `/drives/${drive.id}/root/search(q='${encodeURIComponent(query)}')?$select=id,name,size,webUrl,createdDateTime,lastModifiedDateTime,file,folder,parentReference`
  );
  return result.value;
}
